import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-big-calendar";
import { ReactComponent as DateRangeIcon } from "../../assets/img/events/date.svg";
import { ReactComponent as LocationIcon } from "../../assets/img/events/location.svg";
import { ReactComponent as PriceIcon } from "../../assets/img/events/price.svg";
import { ReactComponent as TimeIcon } from "../../assets/img/events/time.svg";
import { Button } from "components/elements";
import { Dropdown, DropdownContent, DropdownItem } from "components/elements";
import { TrendingRow } from "components/layout/TrendingRow";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";
import {
    CalendarFriendlyEvent,
    dateRangeToDisplay,
    dateToDisplay,
    formatDate,
    timeToDisplay,
} from "./types";
import { AllEvents } from "./AllEvents";
import { EventDetails } from "./EventDetails";
import { EventDetailsModal } from "./EventDetailsModal";
import { EventRegistrationModal } from "./EventRegistrationModal";
import { UpcomingEvents } from "./UpcomingEvents";

dayjs.extend(utc);
dayjs.extend(timezone);

export const Events = () => {
    const timeZone = useUserTimezone();
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [isOpen, setIsOpen] = useState(false);
    const [openEventDetails, setOpenEventDetails] = useState(false);
    const [titleSearch, setTitleSearch] = useState("");
    const [locationSearch, setLocationSearch] = useState("");
    const [hostSearch, setHostSearch] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    type DateRangeWithNull = {
        start: Date | null;
        end: Date | null;
    };

    const [dateRange, setDateRange] = useState<DateRangeWithNull>({
        start: dayjs().startOf("month").toDate(),
        end: dayjs().endOf("month").toDate(),
    });

    const { data: futureEvents = [] } = trpc.events.allFutureEvents.useQuery();

    const futureEventsFormatted = useMemo(
        () =>
            futureEvents.map((item) => ({
                ...item,
                start: new Date(item.start),
                end: item.end ? new Date(item.end) : new Date(item.start),
            })),
        [futureEvents],
    );

    const filteredEvents = useMemo(() => {
        return futureEventsFormatted.filter((event) => {
            const matchesTitle =
                !titleSearch || event.title.toLowerCase().includes(titleSearch.toLowerCase());
            const matchesLocation =
                !locationSearch ||
                (event.location &&
                    event.location.toLowerCase().includes(locationSearch.toLowerCase()));
            const matchesHost =
                !hostSearch ||
                (event.host && event.host.toLowerCase().includes(hostSearch.toLowerCase()));

            const matchesMonth =
                selectedDate &&
                event.start.getMonth() === selectedDate.getMonth() &&
                event.start.getFullYear() === selectedDate.getFullYear();

            const hasDateRange = dateRange.start || dateRange.end;

            const matchesDateRange =
                !hasDateRange ||
                ((!dateRange.start || event.start >= dateRange.start) &&
                    (!dateRange.end || event.start <= dateRange.end));

            // Use range if set, otherwise month
            const matchesDateFilter = hasDateRange
                ? matchesDateRange
                : !selectedDate || matchesMonth;

            return matchesTitle && matchesLocation && matchesHost && matchesDateFilter;
        });
    }, [futureEventsFormatted, titleSearch, locationSearch, hostSearch, dateRange, selectedDate]);

    // const filteredByMonth = useMemo(
    //     () =>
    //         filteredEvents.filter((item) => {
    //             if (!selectedDate) return true;
    //             return (
    //                 item.start.getMonth() === selectedDate.getMonth() &&
    //                 item.start.getFullYear() === selectedDate.getFullYear()
    //             );
    //         }),
    //     [filteredEvents, selectedDate],
    // );

    const nextEvent = useMemo(() => {
        if (!futureEventsFormatted.length) return null;
        return futureEventsFormatted.reduce((soonest, current) => {
            return current.start < soonest.start ? current : soonest;
        }, futureEventsFormatted[0]);
    }, [futureEventsFormatted]);

    // Generate month options for the next 12 months
    const monthOptions = useMemo(() => {
        const months = [];
        const today = new Date();

        for (let i = 0; i < 12; i++) {
            const date = dayjs().add(i, "month");
            months.push({
                value: date.toDate(),
                label: date.format("MMMM YYYY"),
                key: date.format("YYYY-MM"),
            });
        }

        return months;
    }, []);

    const [selectedEvent, setSelectedEvent] = useState<CalendarFriendlyEvent | null>(null);

    const onSelectEvent = useCallback(
        (event: CalendarFriendlyEvent) => {
            setSelectedEvent((currentEvent) => {
                if (currentEvent?.id === event.id) {
                    return null;
                }
                return event;
            });
        },
        [setSelectedEvent],
    );

    const onOpenRegistrationModal = (event: CalendarFriendlyEvent) => {
        setSelectedEvent(event);
        setOpenEventDetails(false);
        setIsOpen(true);
    };

    const onOpenEventDetails = (event: CalendarFriendlyEvent) => {
        setSelectedEvent(event);
        setIsOpen(false);
        setOpenEventDetails(true);
    };

    const handleMonthSelect = (monthDate: Date | null) => {
        setSelectedDate(monthDate);
        setDateRange({ start: null, end: null });
    };

    console.log("Filtered Events:", futureEventsFormatted);

    return (
        <>
            {selectedEvent !== null && (
                <EventRegistrationModal isOpen={isOpen} onClose={setIsOpen} event={selectedEvent} />
            )}
            {selectedEvent !== null && (
                <EventDetailsModal
                    isOpen={openEventDetails}
                    onClose={setOpenEventDetails}
                    event={selectedEvent}
                />
            )}
            <div className="mt-6 font-sans text-[28px] font-semibold leading-normal text-[#424242]">
                Events
            </div>
            <TrendingRow filterType="event" title="Trending Events" limit={5} />
            <div className="mx-auto flex w-full gap-6 py-6">
                {/* Main Content — Next Event & Event List */}
                <main className="flex flex-grow flex-col gap-6">
                    <div className="flex flex-col justify-between rounded-2xl border border-[#92cee4] bg-[#dff1fa] p-6">
                        <h2 className="font-roboto mb-9 text-[20px] font-semibold leading-[100%] text-[#424242]">
                            Next Event
                        </h2>
                        {nextEvent && (
                            <div className="flex items-end justify-between gap-4">
                                <div>
                                    <p className="mb-4 font-sans text-[18px] font-semibold leading-[100%] text-[#424242]">
                                        {nextEvent.title}
                                    </p>
                                    <p className="mb-9 font-sans text-[14px] font-normal leading-[125%] text-[#424242]/90">
                                        {nextEvent.description}
                                    </p>
                                    <div className="flex items-center gap-5 font-sans text-[14px] font-normal leading-[100%] tracking-[0] text-[#424242]/90">
                                        {nextEvent.start && (
                                            <div className="flex flex-row items-center gap-1">
                                                <DateRangeIcon className="h-5 w-5" />
                                                <div>
                                                    {dateRangeToDisplay(
                                                        nextEvent.start,
                                                        nextEvent?.end,
                                                        timeZone,
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {nextEvent.location && (
                                            <div className="flex flex-row items-center gap-0">
                                                <div>
                                                    <LocationIcon className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    {nextEvent.location && (
                                                        <span>{nextEvent.location}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {nextEvent.priceRange && (
                                            <div className="flex flex-row items-center gap-0">
                                                <div>
                                                    <PriceIcon className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    {nextEvent.priceRange && (
                                                        <span>{nextEvent.priceRange}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* All Events */}
                    <AllEvents
                        futureEventsFormatted={futureEventsFormatted}
                        onSelectEvent={onSelectEvent}
                        onOpenModal={onOpenRegistrationModal}
                        onOpenEventDetails={onOpenEventDetails}
                    />
                </main>
                {/* Right Aside */}
                <div className="w-[30%]">
                    <div className="mx-auto flex h-[850px] flex-col rounded-2xl border border-[#c8c8c8] bg-[#fafffe] p-4">
                        {/* Header + Collapse Toggle */}
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-sans  font-semibold text-[#424242]">Filters</h2>
                            <button
                                onClick={() => setShowFilters((prev) => !prev)}
                                className="text-sm font-medium text-[#2DA7E0] hover:underline focus:outline-none"
                            >
                                {showFilters ? "Hide Filters ▲" : "Show Filters ▼"}
                            </button>
                        </div>

                        {/* Filters Collapsible Section */}
                        <div
                            className={`overflow-hidden transition-all duration-300 ${
                                showFilters ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                            }`}
                        >
                            <div className="flex-shrink-0 space-y-4">
                                {/* Title Search */}
                                <div className="flex flex-col gap-2">
                                    <label className="font-sans text-[14px] font-medium text-[#424242]">
                                        Filter by Title
                                    </label>
                                    <input
                                        type="text"
                                        value={titleSearch}
                                        onChange={(e) => setTitleSearch(e.target.value)}
                                        placeholder="Event title..."
                                        className="rounded-lg border border-[#c8c8c8] px-3 py-2 text-sm"
                                    />
                                </div>

                                {/* Location Search */}
                                <div className="flex flex-col gap-2">
                                    <label className="font-sans text-[14px] font-medium text-[#424242]">
                                        Filter by Location
                                    </label>
                                    <input
                                        type="text"
                                        value={locationSearch}
                                        onChange={(e) => setLocationSearch(e.target.value)}
                                        placeholder="Event location..."
                                        className="rounded-lg border border-[#c8c8c8] px-3 py-2 text-sm"
                                    />
                                </div>

                                {/* Host Search */}
                                <div className="flex flex-col gap-2">
                                    <label className="font-sans text-[14px] font-medium text-[#424242]">
                                        Filter by Host
                                    </label>
                                    <input
                                        type="text"
                                        value={hostSearch}
                                        onChange={(e) => setHostSearch(e.target.value)}
                                        placeholder="Event host..."
                                        className="rounded-lg border border-[#c8c8c8] px-3 py-2 text-sm"
                                    />
                                </div>

                                {/* Date Range */}
                                <div className="flex flex-col gap-2 ">
                                    <label className="font-sans text-[14px] font-medium text-[#424242]">
                                        Date Range
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="date"
                                            value={
                                                dateRange.start
                                                    ? dateRange.start.toISOString().split("T")[0]
                                                    : ""
                                            }
                                            onChange={(e) => {
                                                setSelectedDate(null);
                                                setDateRange((prev) => ({
                                                    ...prev,
                                                    start: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                }));
                                            }}
                                            className="flex-1 rounded-lg border border-[#c8c8c8] px-3 py-2 text-sm"
                                        />
                                        <input
                                            type="date"
                                            value={
                                                dateRange.end
                                                    ? dateRange.end.toISOString().split("T")[0]
                                                    : ""
                                            }
                                            onChange={(e) =>
                                                setDateRange((prev) => ({
                                                    ...prev,
                                                    end: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                }))
                                            }
                                            className="flex-1 rounded-lg border border-[#c8c8c8] px-3 py-2 text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Month Selector */}
                                <div className="mt-3 flex flex-col gap-2">
                                    <label className="font-sans text-[14px] font-medium text-[#424242]">
                                        Filter by Month
                                    </label>
                                    <Dropdown
                                        content={
                                            <DropdownContent className="max-h-60 w-full overflow-y-auto">
                                                <DropdownItem
                                                    onSelect={() => handleMonthSelect(null)}
                                                    className="cursor-pointer px-3 py-2 text-red-600 hover:bg-red-50"
                                                >
                                                    Clear
                                                </DropdownItem>
                                                {monthOptions.map((month) => (
                                                    <DropdownItem
                                                        key={month.key}
                                                        onSelect={() =>
                                                            handleMonthSelect(month.value)
                                                        }
                                                        className={`cursor-pointer px-3 py-2 ${
                                                            dayjs(selectedDate)
                                                                .tz(timeZone)
                                                                .format("YYYY-MM") === month.key
                                                                ? "bg-[#2DA7E0] text-white"
                                                                : ""
                                                        }`}
                                                    >
                                                        {month.label}
                                                    </DropdownItem>
                                                ))}
                                            </DropdownContent>
                                        }
                                    >
                                        <Button
                                            variant="outline"
                                            className="w-full justify-between"
                                        >
                                            {selectedDate
                                                ? dayjs(selectedDate)
                                                      .tz(timeZone)
                                                      .format("MMMM YYYY")
                                                : "All Months"}
                                            <svg
                                                className="ml-2 h-4 w-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </Button>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>

                        {/* Upcoming Events */}
                        <div className="mt-4 flex min-h-0 flex-1 flex-col">
                            <h2 className="mb-4 font-sans text-[16px] font-semibold text-[#424242]">
                                Upcoming Events
                            </h2>
                            <div className="flex-1 overflow-y-auto pr-2">
                                <UpcomingEvents
                                    futureEventsFormatted={filteredEvents}
                                    onSelectEvent={onSelectEvent}
                                    onOpenModal={onOpenRegistrationModal}
                                    onOpenEventDetails={onOpenEventDetails}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
